import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import {
  categoryData,
  categorySlugs,
  categoryInfo,
} from "@/mocks/category";

const allCategoryNames = ["热门综艺", "搞笑综艺", "真人秀综艺", "音乐综艺", "脱口秀", "竞技综艺", "旅行综艺"];

const slugToTab: Record<string, string> = {
  remen: "热门综艺",
  gaoxiao: "搞笑综艺",
  zhenrenxiu: "真人秀综艺",
  yinyue: "音乐综艺",
  tuokouxiu: "脱口秀",
  jingji: "竞技综艺",
  lvxing: "旅行综艺",
};

const tabToSlug: Record<string, string> = {
  "热门综艺": "remen",
  "搞笑综艺": "gaoxiao",
  "真人秀综艺": "zhenrenxiu",
  "音乐综艺": "yinyue",
  "脱口秀": "tuokouxiu",
  "竞技综艺": "jingji",
  "旅行综艺": "lvxing",
};

function getAllShows() {
  const all: Array<{
    id: number;
    title: string;
    image: string;
    description: string;
    category: string;
    categorySlug: string;
    views: string;
    rating: number;
    episode: string;
    date: string;
  }> = [];
  for (const slug of categorySlugs) {
    const cat = categoryData[slug as keyof typeof categoryData];
    if (cat) {
      for (const show of cat.shows) {
        all.push({ ...show, category: cat.name, categorySlug: slug });
      }
    }
  }
  return all.sort(
    (a, b) => parseInt(b.views.replace(/[^0-9]/g, "")) - parseInt(a.views.replace(/[^0-9]/g, ""))
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const validSlug = slug && slugToTab[slug] ? slug : "remen";
  const currentTab = slugToTab[validSlug];

  const [activeTab, setActiveTab] = useState(currentTab);

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    const newSlug = tabToSlug[tabName] || "remen";
    navigate(`/category/${newSlug}`);
  };

  const isRemen = validSlug === "remen";

  const catInfo = isRemen
    ? { name: "热门综艺", description: "全网最火的综艺节目合集，热播推荐一网打尽", keywords: "热门综艺,热播综艺,综艺推荐,热播节目,人气综艺" }
    : categoryInfo[validSlug as keyof typeof categoryInfo] || {
        name: "热门综艺",
        description: "全网最火的综艺节目合集",
        keywords: "热门综艺",
      };

  const shows = isRemen
    ? getAllShows()
    : (categoryData[validSlug as keyof typeof categoryData]?.shows || []);

  return (
    <>
      <SEOHead
        title={`${catInfo.name} - 6789影视 | ${catInfo.description}`}
        description={`6789影视${catInfo.name}频道，${catInfo.description}。海量${catInfo.name}免费全集在线观看，高清流畅。`}
        keywords={(catInfo as { keywords?: string }).keywords || catInfo.name}
        canonicalUrl={`https://6789ys.com/category/${validSlug}`}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto py-4">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                { name: catInfo.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto py-8 md:py-12">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
              {catInfo.name}
            </h1>
            <p className="text-sm text-foreground-600">{catInfo.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {allCategoryNames.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeTab === tab
                    ? "bg-primary-500 text-white"
                    : "bg-white text-foreground-600 border border-background-200 hover:border-primary-300 hover:text-primary-500"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6" data-product-shop="">
            {shows.map((show) => (
              <Link
                key={show.id}
                to={`/article/${show.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer no-underline flex flex-col"
              >
                <div className="relative overflow-hidden aspect-[3/2]">
                  <img
                    src={show.image}
                    alt={show.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    title={`${show.title} - 6789影视`}
                  />
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    {show.rating}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <span className="text-white text-xs font-medium">
                      {show.episode}
                    </span>
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <span className="text-xs text-primary-500 font-medium mb-1">
                    {show.category}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {show.title}
                  </h3>
                  <p className="text-xs text-foreground-600 line-clamp-2 mb-3 flex-1">
                    {show.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {show.views}
                    </span>
                    <span>{show.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}